/**
 * Alpine.js Status & Post Composer Component
 * Post, Trip and Status modes in one overlay: a centered modal from the sm breakpoint, a bottom sheet below it.
 * Edit mode renders the post body inline instead.
 */

import { editorBase } from './alpine-editor-base.js';
import { uploadPhoto, parseUploadError, validateFile, PhotoUploadConfig } from './photo-uploader.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { weekStartForLocale, weekdayKeys, monthGrid, nightsBetween, pickRangeDate, addMonths, parseIsoDate, toIsoDate } from './range-calendar.js';
import { searchCities, cityDetail } from './city-search.js';
import { siteLocale } from './site-locale.js';
import { followApiPhoneGateRefusal, PHONE_VERIFICATION_REQUIRED } from './phone-gate-refusal.js';
import { belongsToOtherDialog, guardDialogFocus, trapTabFocus, watchKeyboardInset } from './composer-dialog.js';

function t(key, params) {
    return window.tOr(key, key, params);
}

// The /v1 envelope nests its text under `error`. Reading `error` itself
// renders the object, so the fallback carries the only text a user can read.
function apiErrorMessage(body) {
    const nested = body && typeof body.error === 'object' && body.error !== null ? body.error.message : null;
    const flat = body ? body.message : null;
    if (typeof nested === 'string' && nested !== '') return nested;
    if (typeof flat === 'string' && flat !== '') return flat;

    return t('common.post_save_failed');
}

// The month grid is Gregorian with Latin digits, as on the trip editor: fa-IR and th-TH default to others.
function dateFormat(options) {
    return new Intl.DateTimeFormat(siteLocale(), { calendar: 'gregory', numberingSystem: 'latn', ...options });
}

function formatNumber(value) {
    return new Intl.NumberFormat(siteLocale(), { numberingSystem: 'latn' }).format(value);
}

/**
 * Status & Post Composer Component Factory
 */
export function statusPostComposer() {
    return {
        MODES: {
            STATUS: 'status',
            POST: 'post',
            TRIP: 'trip'
        },

        LIMITS: {
            STATUS: 140,
            POST: 1000,
            TRIP: 500
        },

        ENDPOINTS: {
            STATUS: '/app/helpers/status',
            POST_API: '/v1/post',
            TRIP_API: '/v1/trip'
        },

        VISIBILITY_VALUES: ['public', 'users', 'friends', 'me'],

        MAX_PHOTOS: 10,
        MAX_FILE_SIZE: PhotoUploadConfig.MAX_FILE_SIZE,
        ALLOWED_TYPES: PhotoUploadConfig.ALLOWED_TYPES,
        CHAR_LIMIT_WARNING_THRESHOLD: 0.9,
        MAX_TEXTAREA_HEIGHT_POST: 320,
        MAX_TEXTAREA_HEIGHT_STATUS: 160,
        UPLOAD_CHECK_INTERVAL_MS: 500,
        UPLOAD_TIMEOUT_MS: 60000,
        CITY_SEARCH_DELAY_MS: 250,
        FLASH_MS: 1200,

        // Reactive state
        mode: 'post',
        content: '',
        title: '',
        savedStatus: '',
        // Per-mode content storage to preserve data when switching modes
        postContent: '',
        tripContent: '',
        // null means no status draft. An empty string is a draft that clears the saved status.
        statusContent: null,
        photos: [],
        photosById: new Map(),
        visibility: 'public',
        isSaving: false,
        isSubmittingPost: false,
        showSuccess: false,
        successMessage: '',
        error: null,
        userId: null,
        username: null,
        uploadControllers: new Map(),
        uploadCheckInterval: null,
        uploadTimeout: null,

        // Overlay state
        isOpen: false,
        subScreen: null, // null | 'city' | 'dates'
        audienceOpen: false,
        typeGridOpen: false,
        flashRows: [],
        keyboardInset: 0,
        _flashTimer: null,
        _returnFocus: null,
        _stopKeyboardWatch: null,
        _stopFocusGuard: null,
        _readGeneration: 0,
        pendingReads: 0,
        _queueCancelled: false,
        _closeAfterCancel: false,
        _uploadWaitResolve: null,

        // Trip mode state
        tripCityId: null,
        tripCityName: '',
        tripCityDetail: '',
        tripCountryCode: null,
        tripStartDate: null,
        tripEndDate: null,
        tripLookingFor: [],
        tripType: null,

        // City search state
        cityQuery: '',
        cityResults: [],
        citySearching: false,
        citySearched: false,
        _cityTimer: null,
        _cityAbort: null,

        // Calendar state
        calYear: new Date().getFullYear(),
        calMonth: new Date().getMonth(),
        weekStart: null,

        // Edit mode state
        isEditMode: false,
        editPostId: null,
        editFeedOwnerId: null,
        photosToDelete: [],
        showTitleField: false,
        onCancelCallback: null,
        onCompleteCallback: null,

        // Computed properties
        get isStatusMode() {
            return this.mode === this.MODES.STATUS;
        },

        get isPostMode() {
            return this.mode === this.MODES.POST;
        },

        get isTripMode() {
            return this.mode === this.MODES.TRIP;
        },

        get maxLength() {
            if (this.isStatusMode) return this.LIMITS.STATUS;
            if (this.isTripMode) return this.LIMITS.TRIP;
            return this.LIMITS.POST;
        },

        get charCount() {
            return this.content.length;
        },

        get counterText() {
            return `${formatNumber(this.charCount)}/${formatNumber(this.maxLength)}`;
        },

        get isNearLimit() {
            return this.charCount > this.maxLength * this.CHAR_LIMIT_WARNING_THRESHOLD;
        },

        get hasContent() {
            return this.content.trim().length > 0;
        },

        get hasPhotos() {
            return this.photos.length > 0;
        },

        get missingTripRows() {
            const rows = [];
            if (!this.tripCityId) rows.push('city');
            if (!this.tripStartDate || !this.tripEndDate) rows.push('dates');
            return rows;
        },

        get canSave() {
            if (this.isSaving || this.isSubmittingPost) return false;
            if (this.isPostMode) {
                if (this.failedPhotosCount > 0 || this.pendingReads > 0) {
                    return false;
                }
                return this.hasContent || this.hasPhotos;
            }
            if (this.isTripMode) {
                return this.missingTripRows.length === 0;
            }
            return this.content.trim() !== (this.savedStatus || '').trim();
        },

        // Only the wait for uploads can be cancelled. The token fetch and the request cannot.
        get isQueued() {
            return this.isSubmittingPost && this._uploadWaitResolve !== null;
        },

        get uploadingPhotosCount() {
            return this.photos.filter(p => p.uploading).length;
        },

        get failedPhotosCount() {
            return this.photos.filter(p => p.error).length;
        },

        // A draft in any mode, not only the visible one, is what a close would lose.
        get hasDraft() {
            const post = this.isPostMode ? this.content : this.postContent;
            const trip = this.isTripMode ? this.content : this.tripContent;
            const status = this.isStatusMode ? this.content : this.statusContent;
            const hasTripData = this.tripCityId || this.tripStartDate || this.tripEndDate || this.tripType || this.tripLookingFor.length > 0;
            const statusChanged = status !== null && status.trim() !== (this.savedStatus || '').trim();

            return post.trim() !== '' || this.title.trim() !== '' || this.hasPhotos || this.pendingReads > 0 || trip.trim() !== '' || Boolean(hasTripData) || statusChanged;
        },

        get placeholder() {
            // Reference $store.i18n.ready to re-compute when translations load
            void this.$store?.i18n?.ready;
            if (this.isPostMode) return t('common.post_placeholder_default');
            if (this.isTripMode) return t('common.post_trip_details');
            return t('common.post_placeholder_status');
        },

        get tripDateDisplay() {
            void this.$store?.i18n?.ready;
            const start = parseIsoDate(this.tripStartDate);
            const end = parseIsoDate(this.tripEndDate);
            if (!start || !end) return '';
            const format = dateFormat({ month: 'short', day: 'numeric' });
            const range = typeof format.formatRange === 'function'
                ? format.formatRange(start, end)
                : `${format.format(start)} - ${format.format(end)}`;

            return `${range} · ${this.tripNightsLabel}`;
        },

        get tripNightsLabel() {
            void this.$store?.i18n?.ready;
            const nights = nightsBetween(this.tripStartDate, this.tripEndDate);
            if (nights === null) return '';
            if (nights === 0) return t('common.post_trip_day_trip');
            // The _plural value selects on `form` in locales with more than two plural forms.
            const params = { count: formatNumber(nights), form: new Intl.PluralRules(siteLocale()).select(nights) };

            return t(nights === 1 ? 'common.post_trip_nights' : 'common.post_trip_nights_plural', params);
        },

        get tripTypeKey() {
            return this.tripType ? `profile.trip_type_${this.tripType}` : null;
        },

        // _overlay.twig reserves a fixed height for the open menu. More options must raise that reserve.
        get visibilityOptions() {
            void this.$store?.i18n?.ready;
            return [
                { value: 'public', label: t('common.post_visibility_all_users'), icon: 'fa-globe' },
                { value: 'users', label: t('common.post_visibility_members'), icon: 'fa-users' },
                { value: 'friends', label: t('common.post_visibility_my_friends'), icon: 'fa-user-group' },
                { value: 'me', label: t('common.post_visibility_only_me'), icon: 'fa-lock' }
            ];
        },

        get currentVisibility() {
            return this.visibilityOptions.find(o => o.value === this.visibility) || this.visibilityOptions[0];
        },

        get currentVisibilityLabel() {
            return this.currentVisibility.label;
        },

        get saveButtonText() {
            void this.$store?.i18n?.ready;
            if (this.showSuccess) return t('common.post_saved_success');
            if (this.isSaving && !this.isSubmittingPost) return t('common.post_saving');
            if (this.uploadingPhotosCount > 0) {
                const key = this.uploadingPhotosCount > 1 ? 'common.post_uploading_photos_plural' : 'common.post_uploading_photos';
                return t(key, { count: this.uploadingPhotosCount });
            }
            if (this.isSubmittingPost) {
                return this.isEditMode ? t('common.post_updating') : t('common.post_posting');
            }
            if (this.isEditMode) return t('common.post_button_update');
            if (this.isStatusMode) return t('common.post_button_update');
            if (this.isTripMode) return t('common.post_share_trip');
            return t('common.post_button_post');
        },

        get weekdayLabels() {
            void this.$store?.i18n?.ready;
            return weekdayKeys(this.weekStart ?? 0).map(key => ({ key, label: t(`common.${key}`) }));
        },

        get calendarMonths() {
            const today = toIsoDate(new Date());
            const title = dateFormat({ month: 'long', year: 'numeric' });

            return [0, 1].map(offset => {
                const { year, month } = addMonths(this.calYear, this.calMonth, offset);
                return {
                    key: `${year}-${month}`,
                    title: title.format(new Date(year, month, 1)),
                    cells: monthGrid(year, month, {
                        weekStart: this.weekStart ?? 0,
                        today,
                        start: this.tripStartDate,
                        end: this.tripEndDate,
                        minDate: today
                    })
                };
            });
        },

        get canGoPrevMonth() {
            const now = new Date();
            return this.calYear > now.getFullYear() || (this.calYear === now.getFullYear() && this.calMonth > now.getMonth());
        },

        // Methods
        init(userId, username, initialStatus = '', config = {}) {
            // Alpine.js automatically calls init() without params if the method exists.
            // Skip initialization if called without required parameters - the x-init
            // directive will call this method again with proper values.
            if (!userId || !username) {
                return;
            }

            this.userId = userId;
            this.username = username;
            this.savedStatus = initialStatus || '';
            if (config.statusMaxLength) {
                this.LIMITS = { ...this.LIMITS, STATUS: config.statusMaxLength };
            }
            // Every post by the current user mounts an edit-mode composer, so a page of
            // posts adds one of these per post. Keep the handler to drop it in destroy().
            this._savedFieldHandler = (event) => this.onFieldSaved(event.detail);
            window.addEventListener(editorBase.SAVED_EVENT, this._savedFieldHandler);

            if (config.editMode) {
                this.isEditMode = true;
                this.editPostId = config.postId;
                this.editFeedOwnerId = config.feedOwnerId;
                this.content = config.message || '';
                this.title = config.title || '';
                this.visibility = config.visibility || 'public';
                this.showTitleField = !!config.title || config.showTitleField;
                this.onCancelCallback = config.onCancel || null;
                this.onCompleteCallback = config.onComplete || null;

                if (config.photos && config.photos.length > 0) {
                    this.loadExistingPhotos(config.photos);
                }

                this.mode = this.MODES.POST;
            }

            this.$nextTick(() => {
                this.autoResizeTextarea();
                const textarea = this.$refs.textarea;
                if (textarea) {
                    let width = textarea.clientWidth;
                    this._textareaObserver = new ResizeObserver(() => {
                        if (textarea.clientWidth === width) return;
                        width = textarea.clientWidth;
                        this.autoResizeTextarea();
                    });
                    this._textareaObserver.observe(textarea);
                }
            });

            this.$watch('content', () => {
                this.$nextTick(() => {
                    this.autoResizeTextarea();
                });
            });
        },

        loadExistingPhotos(photos) {
            // Convert existing post photos to the format expected by the component
            // Photo URLs are already constructed in the backend, just use them directly
            // IMPORTANT: Keep photo IDs as strings to preserve precision
            this.photos = photos.map((photo, index) => ({
                id: String(photo.photo_id) || Date.now() + index,
                uploadedPhotoId: String(photo.photo_id),
                thumbnailUrl: photo.url_medium || photo.url_small || photo.url,
                dataUrl: null,
                uploading: false,
                error: null,
                isExisting: true,
                file: null
            }));

            // Populate photosById map
            this.photos.forEach(photo => {
                this.photosById.set(photo.id, photo);
            });
        },

        toggleTitleField() {
            this.showTitleField = !this.showTitleField;
            if (!this.showTitleField) {
                this.title = '';
            }
        },

        // Overlay
        open(targetMode = null) {
            if (this.isEditMode) return;
            if (!this.isOpen) {
                this._returnFocus = document.activeElement;
                this.isOpen = true;
                this.showSuccess = false;
                lockBodyScroll();
                this._stopKeyboardWatch = watchKeyboardInset((inset) => {
                    this.keyboardInset = inset;
                });
                this._stopFocusGuard = guardDialogFocus(() => this.$refs.overlay, () => this.focusCurrentScreen());
            }
            if (targetMode && targetMode !== this.mode) {
                this.switchMode(targetMode);
            }
            this.afterPaint(() => {
                window.LucideIcons?.initialize(this.$refs.overlay);
                this.focusModeField();
            });
        },

        // x-show reveals an element after $nextTick resolves, so a focus move waits one frame more.
        afterPaint(fn) {
            this.$nextTick(() => requestAnimationFrame(() => {
                if (this.isOpen) fn();
            }));
        },

        stopDialogWatchers() {
            this._stopKeyboardWatch?.();
            this._stopKeyboardWatch = null;
            this._stopFocusGuard?.();
            this._stopFocusGuard = null;
        },

        // The edit-mode composer has no overlay, and $refs also reaches refs of enclosing components.
        scopeEl() {
            return this.$refs.overlay ? this.$refs.panel : this.$el;
        },

        focusCurrentScreen() {
            if (this.subScreen === 'city') {
                this.$refs.cityInput?.focus();
            } else if (this.subScreen === 'dates') {
                this.$refs.panel?.querySelector('[data-dates-back]')?.focus();
            } else {
                this.focusModeField();
            }
        },

        close() {
            if (!this.isOpen) return;
            this.isOpen = false;
            clearTimeout(this._flashTimer);
            this.flashRows = [];
            this.subScreen = null;
            this.audienceOpen = false;
            this.typeGridOpen = false;
            this.error = null;
            this.cancelCitySearch();
            this.dropLoneStartDate();
            unlockBodyScroll();
            this.stopDialogWatchers();
            const target = this._returnFocus;
            this._returnFocus = null;
            if (target && typeof target.focus === 'function') {
                this.$nextTick(() => requestAnimationFrame(() => {
                    if (!this.isOpen) target.focus();
                }));
            }
        },

        requestClose() {
            if (this.isQueued) {
                this._closeAfterCancel = true;
                this.cancelQueuedSubmit();
                return;
            }
            if (this.isSaving || this.isSubmittingPost) return;
            if (!this.hasDraft) {
                this.close();
                return;
            }
            if (!window.ConfirmModal) {
                this.close();
                return;
            }
            window.ConfirmModal.show({
                title: t('common.post_discard_title'),
                message: t('common.post_discard_body'),
                confirmText: t('common.discard'),
                cancelText: t('common.keep_editing'),
                type: 'danger',
                focus: 'cancel',
                zIndex: 10002,
                onConfirm: () => this.discardAll(),
                onCancel: () => this.afterPaint(() => this.focusCurrentScreen())
            });
        },

        // Pending reads and uploads belong to the draft a discard, a save or a destroy drops.
        dropPhotoWork() {
            this._readGeneration += 1;
            this.pendingReads = 0;
            this.uploadControllers.forEach(controller => controller.abort());
            this.uploadControllers.clear();
        },

        discardAll() {
            this.dropPhotoWork();
            this.content = '';
            this.title = '';
            this.showTitleField = false;
            this.postContent = '';
            this.tripContent = '';
            this.statusContent = null;
            this.photos = [];
            this.photosById.clear();
            this.photosToDelete = [];
            this.clearTripData();
            if (this.isStatusMode) {
                this.content = this.savedStatus;
            }
            this.close();
        },

        // Escape closes the innermost layer first. A dialog stacked above owns Escape while it shows.
        handleEscape(event) {
            if (!this.isOpen || event?.isComposing || belongsToOtherDialog(event?.target, this.$refs.overlay)) return;
            if (this.audienceOpen) {
                this.closeAudience();
            } else if (this.typeGridOpen) {
                this.typeGridOpen = false;
                this.$nextTick(() => this.focusTripRow('type'));
            } else if (this.subScreen) {
                this.back();
            } else {
                this.requestClose();
            }
        },

        trapFocus(event) {
            trapTabFocus(this.$refs.panel, event);
        },

        focusModeField() {
            if (this.isTripMode) {
                this.$refs.panel?.querySelector('[data-trip-first-row]')?.focus();
                return;
            }
            const textarea = this.$refs.textarea;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        },

        back() {
            const from = this.subScreen;
            this.subScreen = null;
            this.cancelCitySearch();
            this.dropLoneStartDate();
            this.afterPaint(() => {
                window.LucideIcons?.initialize(this.$refs.overlay);
                this.focusTripRow(from);
            });
        },

        focusTripRow(row) {
            this.$refs.panel?.querySelector(`[data-trip-row="${row}"]`)?.focus();
        },

        // A start date without an end date is no trip date, so leaving the dates screen drops it.
        dropLoneStartDate() {
            if (this.tripStartDate && !this.tripEndDate) {
                this.tripStartDate = null;
            }
        },

        // An open on-screen keyboard leaves the body less room than the menu, so opening the menu ends typing first.
        // Focus moves to the pill rather than blurring, so it stays inside the dialog's focus trap.
        toggleAudience(trigger) {
            this.audienceOpen = !this.audienceOpen;
            const active = document.activeElement;
            if (this.audienceOpen && this.keyboardInset > 0 && active?.matches?.('input, textarea')) {
                trigger?.focus({ preventScroll: true });
            }
        },

        setVisibility(value) {
            this.visibility = value;
            this.closeAudience();
        },

        // The open menu holds focus, so closing it returns focus to the pill.
        closeAudience() {
            this.audienceOpen = false;
            this.$nextTick(() => this.scopeEl()?.querySelector('[data-audience-trigger]')?.focus());
        },

        flash(rows) {
            this.flashRows = rows;
            clearTimeout(this._flashTimer);
            this._flashTimer = setTimeout(() => {
                this.flashRows = [];
            }, this.FLASH_MS);
        },

        attemptSave() {
            if (this.isTripMode && this.missingTripRows.length > 0 && !this.isSaving) {
                const missing = this.missingTripRows;
                this.flash(missing);
                this.focusTripRow(missing[0]);
                return;
            }
            this.save();
        },

        // Trip: city sub-screen
        openCity() {
            this.cancelCitySearch();
            this.subScreen = 'city';
            this.cityQuery = '';
            this.cityResults = [];
            this.citySearched = false;
            this.afterPaint(() => {
                window.LucideIcons?.initialize(this.$refs.overlay);
                this.$refs.cityInput?.focus();
            });
        },

        cancelCitySearch() {
            clearTimeout(this._cityTimer);
            this._cityTimer = null;
            this._cityAbort?.abort();
            this._cityAbort = null;
            this.citySearching = false;
        },

        onCityInput() {
            this.cancelCitySearch();
            // Results belong to the query that fetched them, so Enter never picks a stale city.
            this.cityResults = [];
            const query = this.cityQuery.trim();
            if (query === '') {
                this.citySearched = false;
                return;
            }
            this.citySearching = true;
            this._cityTimer = setTimeout(async () => {
                const controller = new AbortController();
                this._cityAbort = controller;
                try {
                    const results = await searchCities(query, { signal: controller.signal });
                    if (controller.signal.aborted) return;
                    this.cityResults = results.map(city => ({ ...city, detail: cityDetail(city) }));
                } catch (error) {
                    if (controller.signal.aborted) return;
                    console.error('City search error:', error);
                    this.cityResults = [];
                }
                this.citySearching = false;
                this.citySearched = true;
            }, this.CITY_SEARCH_DELAY_MS);
        },

        chooseCity(city) {
            this.tripCityId = String(city.id);
            this.tripCityName = city.name;
            this.tripCityDetail = city.detail || cityDetail(city);
            this.tripCountryCode = city.countryIso ? city.countryIso.toLowerCase() : null;
            this.back();
        },

        chooseFirstCity() {
            if (!this.citySearching && this.cityResults.length > 0) {
                this.chooseCity(this.cityResults[0]);
            }
        },

        clearCity() {
            this.tripCityId = null;
            this.tripCityName = '';
            this.tripCityDetail = '';
            this.tripCountryCode = null;
        },

        // A clear button hides itself, so focus moves to the row it belongs to.
        clearTripRow(row) {
            if (row === 'city') {
                this.clearCity();
            } else if (row === 'dates') {
                this.clearStartDate();
            } else if (row === 'type') {
                this.tripType = null;
            }
            this.$nextTick(() => this.focusTripRow(row));
        },

        // Trip: dates sub-screen
        openDates() {
            if (this.weekStart === null) {
                this.weekStart = weekStartForLocale(siteLocale());
            }
            const anchor = parseIsoDate(this.tripStartDate) || new Date();
            this.calYear = anchor.getFullYear();
            this.calMonth = anchor.getMonth();
            this.subScreen = 'dates';
            this.afterPaint(() => {
                window.LucideIcons?.initialize(this.$refs.overlay);
                this.$refs.panel?.querySelector('[data-dates-back]')?.focus();
            });
        },

        pickDay(dateStr) {
            const next = pickRangeDate(this.tripStartDate, this.tripEndDate, dateStr);
            this.tripStartDate = next.start;
            this.tripEndDate = next.end;
        },

        clearStartDate() {
            this.tripStartDate = null;
            this.tripEndDate = null;
        },

        clearEndDate() {
            this.tripEndDate = null;
        },

        // On the dates screen a clear button hides itself, so focus moves to the back button.
        clearDates(which) {
            if (which === 'end') {
                this.clearEndDate();
            } else {
                this.clearStartDate();
            }
            this.$nextTick(() => this.$refs.panel?.querySelector('[data-dates-back]')?.focus());
        },

        prevMonth() {
            if (!this.canGoPrevMonth) return;
            ({ year: this.calYear, month: this.calMonth } = addMonths(this.calYear, this.calMonth, -1));
            // The previous button disables itself on the first month and drops its focus.
            if (!this.canGoPrevMonth) {
                this.$nextTick(() => this.$refs.panel?.querySelector('[data-month-next]')?.focus());
            }
        },

        nextMonth() {
            ({ year: this.calYear, month: this.calMonth } = addMonths(this.calYear, this.calMonth, 1));
        },

        // The picked and band state read the trip dates. dark-overrides.scss repaints bg-sky-100.
        isPicked(cell) {
            return !cell.empty && (cell.dateStr === this.tripStartDate || cell.dateStr === this.tripEndDate);
        },

        bandClass(cell) {
            const start = this.tripStartDate;
            const end = this.tripEndDate;
            if (cell.empty || !start || !end || start === end) return '';
            const band = 'bg-sky-100';
            if (cell.dateStr > start && cell.dateStr < end) return band;
            if (cell.dateStr === start) return `${band} rounded-s-full`;
            if (cell.dateStr === end) return `${band} rounded-e-full`;
            return '';
        },

        dayClass(cell) {
            if (this.isPicked(cell)) return 'bg-sky-500 font-semibold text-white';
            if (cell.disabled) return 'bg-transparent text-gray-300 dark:text-gray-600 dark:disabled:!bg-transparent';
            const ink = cell.isToday ? 'font-semibold text-accent-text' : 'text-gray-900 dark:text-gray-100';
            return `bg-transparent ${ink} hover:bg-gray-100 dark:hover:bg-gray-800`;
        },

        formatDayNumber(day) {
            return formatNumber(day);
        },

        formatTileDate(dateStr) {
            const date = parseIsoDate(dateStr);
            if (!date) return '';
            return dateFormat({ weekday: 'short', month: 'short', day: 'numeric' }).format(date);
        },

        formatDayLabel(dateStr) {
            const date = parseIsoDate(dateStr);
            if (!date) return '';
            return dateFormat({ weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).format(date);
        },

        // Trip: type and chips
        toggleTripLookingFor(item) {
            const index = this.tripLookingFor.indexOf(item);
            if (index === -1) {
                this.tripLookingFor.push(item);
            } else {
                this.tripLookingFor.splice(index, 1);
            }
        },

        hasTripLookingFor(item) {
            return this.tripLookingFor.includes(item);
        },

        selectTripType(type) {
            this.tripType = this.tripType === type ? null : type;
            this.typeGridOpen = false;
            this.$nextTick(() => this.focusTripRow('type'));
        },

        clearTripData() {
            this.clearCity();
            this.tripStartDate = null;
            this.tripEndDate = null;
            this.tripLookingFor = [];
            this.tripType = null;
            this.typeGridOpen = false;
        },

        switchMode(newMode) {
            const prevMode = this.mode;
            // A save in flight writes into the mode it started in.
            if (prevMode === newMode || this.isSaving || this.isSubmittingPost) return;

            this.error = null;
            this.audienceOpen = false;
            this.typeGridOpen = false;
            this.subScreen = null;
            this.cancelCitySearch();
            this.dropLoneStartDate();

            if (prevMode === this.MODES.POST) {
                this.postContent = this.content;
            } else if (prevMode === this.MODES.TRIP) {
                this.tripContent = this.content;
            } else if (prevMode === this.MODES.STATUS) {
                this.statusContent = this.content;
            }

            this.mode = newMode;

            if (this.isPostMode) {
                this.content = this.postContent;
            } else if (this.isTripMode) {
                this.content = this.tripContent;
            } else {
                this.content = this.statusContent ?? this.savedStatus ?? '';
            }

            this.$nextTick(() => {
                this.autoResizeTextarea();
                window.LucideIcons?.initialize(this.$refs.overlay || this.$el);
            });
        },

        // A status saved outside the composer replaces savedStatus. A status draft
        // changes only where it still holds the previous saved text.
        onFieldSaved({ field, value }) {
            const previous = this.savedStatus;
            if (field !== 'status' || value === previous) {
                return;
            }
            this.savedStatus = value;
            const previousText = (previous || '').trim();
            if (this.isStatusMode && this.content.trim() === previousText) {
                this.content = value;
            }
            if (this.statusContent !== null && this.statusContent.trim() === previousText) {
                this.statusContent = null;
            }
        },

        clearStatusText() {
            this.content = '';
            this.$refs.textarea?.focus();
        },

        handleKeydown(event) {
            // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to submit
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                if (this.canSave || this.isTripMode) {
                    this.attemptSave();
                }
            }
        },

        autoResizeTextarea() {
            const textarea = this.isTripMode ? this.$refs.tripDescription : this.$refs.textarea;
            if (!textarea || textarea.clientWidth === 0) return;

            // Measured with flex grow off: the overlay textarea grows into its reserved room, and the
            // height must track the text, never that room, or it goes stale when the room shrinks.
            textarea.style.height = '';
            textarea.style.flexGrow = '0';
            const contentHeight = textarea.scrollHeight;
            const overflows = contentHeight > textarea.clientHeight;
            textarea.style.flexGrow = '';
            if (!overflows) return;
            const maxHeight = this.isStatusMode ? this.MAX_TEXTAREA_HEIGHT_STATUS : this.MAX_TEXTAREA_HEIGHT_POST;
            textarea.style.height = Math.min(contentHeight, maxHeight) + 'px';
        },

        async handlePhotoSelect(event) {
            const files = Array.from(event.target.files);

            // Validate photo count
            if (this.photos.length + this.pendingReads + files.length > this.MAX_PHOTOS) {
                this.error = t('common.post_max_photos_error', { count: this.MAX_PHOTOS });
                event.target.value = '';
                return;
            }

            for (const file of files) {
                // Validate file using shared validator
                const validation = validateFile(file);
                if (!validation.valid) {
                    this.error = `${file.name}: ${validation.error}`;
                    event.target.value = '';
                    return;
                }

                // Create photo object with preview
                const reader = new FileReader();
                const photoId = Date.now() + Math.random();
                const generation = this._readGeneration;
                this.pendingReads += 1;
                reader.onerror = () => {
                    if (generation !== this._readGeneration) return;
                    this.pendingReads -= 1;
                    this.error = `${file.name}: ${t('common.post_photos_failed')}`;
                };

                reader.onload = async (e) => {
                    // A discard, a save or a destroy drops reads that finish after it.
                    if (generation !== this._readGeneration) return;
                    this.pendingReads -= 1;
                    // Add photo with preview and uploading state
                    const photoObj = {
                        file: file,
                        dataUrl: e.target.result,
                        id: photoId,
                        uploading: true,
                        uploadedPhotoId: null,
                        thumbnailUrl: null,
                        progress: 0,
                        error: null
                    };

                    this.photos.push(photoObj);
                    this.photosById.set(photoId, photoObj);

                    // Start upload immediately using shared uploader
                    await this.doUploadPhoto(photoId, file);
                };
                reader.readAsDataURL(file);
            }

            // Clear input to allow selecting same file again
            event.target.value = '';
        },

        async doUploadPhoto(photoId, file) {
            // Create abort controller for this upload
            const controller = new AbortController();
            this.uploadControllers.set(photoId, controller);

            try {
                const photoData = await uploadPhoto({
                    file,
                    controller,
                    onProgress: (percent) => {
                        this.updatePhotoState(photoId, { progress: percent });
                    }
                });

                // Update photo with uploaded data
                this.updatePhotoState(photoId, {
                    uploading: false,
                    uploadedPhotoId: String(photoData.id),
                    thumbnailUrl: photoData.thumb_medium || photoData.thumb_large || photoData.url_small,
                    progress: 100,
                    error: null
                });
            } catch (error) {
                // Mark photo as failed
                const errorMsg = parseUploadError(error);
                this.updatePhotoState(photoId, {
                    uploading: false,
                    error: errorMsg !== 'Upload cancelled' ? errorMsg : null
                });
            } finally {
                this.uploadControllers.delete(photoId);
            }
        },

        updatePhotoState(photoId, updates) {
            const photo = this.photosById.get(photoId);
            if (!photo) return;

            Object.assign(photo, updates);

            // Trigger reactivity by creating new array reference
            this.photos = [...this.photos];
        },

        removePhoto(photoId) {
            // Abort in-progress upload if any
            const controller = this.uploadControllers.get(photoId);
            if (controller) {
                controller.abort();
                this.uploadControllers.delete(photoId);
            }

            const photo = this.photosById.get(photoId);

            // Track existing photos for deletion on save
            if (photo && photo.isExisting && photo.uploadedPhotoId) {
                this.photosToDelete.push(photo.uploadedPhotoId);
            }

            const hadFocus = document.activeElement?.closest?.('[data-photo-remove]');
            this.photos = this.photos.filter(p => p.id !== photoId);
            this.photosById.delete(photoId);
            // The removed button held focus, so focus moves to the next photo or the photo picker.
            if (hadFocus) {
                this.$nextTick(() => {
                    const scope = this.scopeEl();
                    (scope?.querySelector('[data-photo-remove]') || scope?.querySelector('[data-photo-input]'))?.focus();
                });
            }
        },

        retryUpload(photoId) {
            const photo = this.photosById.get(photoId);
            if (!photo || !photo.file) return;

            // Reset photo state
            this.updatePhotoState(photoId, {
                uploading: true,
                progress: 0,
                error: null
            });

            // Retry upload using shared uploader
            this.doUploadPhoto(photoId, photo.file);
        },

        cycleVisibility() {
            const currentIndex = this.VISIBILITY_VALUES.indexOf(this.visibility);
            const nextIndex = (currentIndex + 1) % this.VISIBILITY_VALUES.length;
            this.visibility = this.VISIBILITY_VALUES[nextIndex];
        },

        clear() {
            this.dropPhotoWork();
            this.content = '';
            this.title = '';
            // Clear per-mode content for current mode only
            if (this.isPostMode) {
                this.postContent = '';
            } else if (this.isTripMode) {
                this.tripContent = '';
            } else if (this.isStatusMode) {
                this.statusContent = null;
            }
            this.photos = [];
            this.photosById.clear();
            this.photosToDelete = [];
            this.error = null;
            this.$nextTick(() => {
                this.autoResizeTextarea();
            });
        },

        async save() {
            if (!this.canSave) return;

            this.isSaving = true;
            this.error = null;

            try {
                if (this.isTripMode) {
                    await this.saveTrip();
                } else if (this.isStatusMode) {
                    await this.saveStatus();
                } else {
                    if (this.isEditMode) {
                        await this.updatePost();
                    } else {
                        await this.savePost();
                    }
                }
            } catch (error) {
                // The phone gate is already navigating to the verify page.
                if (error && error.phoneGateRefusal) return;
                if (error && error.queueCancelled) return;
                this.error = error.message || t('common.post_save_failed');
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
                this.isSubmittingPost = false;
                if (this._closeAfterCancel) {
                    this._closeAfterCancel = false;
                    this.requestClose();
                }
            }
        },

        async saveStatus() {
            const csrfToken = editorBase.getCsrfToken();

            const submitted = this.content.trim();
            const formData = new FormData();
            formData.append('status_msg', submitted);
            if (csrfToken) {
                formData.append('csrf_token', csrfToken);
            }

            const response = await fetch(this.ENDPOINTS.STATUS, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(t('common.post_save_failed'));
            }

            // The helper cuts the status to its own limit and answers with the text it
            // stored, so every listener reads what the server holds rather than what was
            // typed. Fall back to the submitted text when the body is not readable.
            let storedStatus = submitted;
            try {
                const saved = await response.json();
                if (typeof saved?.msg === 'string') {
                    storedStatus = saved.msg;
                }
            } catch (error) {
                console.warn('Status response was not JSON:', error.message);
            }

            // Update saved status
            this.savedStatus = storedStatus;
            editorBase.emitSaved('status', this.savedStatus);

            this.statusContent = null;
            if (this.isStatusMode) {
                this.content = this.savedStatus;
            }
            this.close();
            this.announce(this.savedStatus ? t('common.post_status_updated') : t('common.post_status_cleared'));
        },

        async saveTrip() {
            // Get auth token
            const token = await window.getApiToken();
            if (!token) {
                throw new Error(t('common.post_auth_failed'));
            }

            // Build trip data (dates are already YYYY-MM-DD from the date picker)
            const data = {
                city_id: this.tripCityId,
                start_date: this.tripStartDate,
                end_date: this.tripEndDate,
                view_acl: this.visibility === 'public' ? 0 :
                          this.visibility === 'users' ? 1 :
                          this.visibility === 'friends' ? 2 : 3
            };

            // Add description if provided
            if (this.content.trim()) {
                data.description = this.content.trim();
            }

            // Add looking_for tags if any selected
            if (this.tripLookingFor.length > 0) {
                data.looking_for = JSON.stringify(this.tripLookingFor);
            }

            if (this.tripType) {
                data.trip_type = this.tripType;
            }

            const response = await fetch(this.ENDPOINTS.TRIP_API, {
                method: 'POST',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                if (await followApiPhoneGateRefusal(response)) {
                    const refusal = new Error(PHONE_VERIFICATION_REQUIRED);
                    refusal.phoneGateRefusal = true;
                    throw refusal;
                }
                const errorData = await response.json().catch(() => ({}));
                // A per-field error carries the text the server wrote for that field.
                const firstError = errorData.errors ? Object.values(errorData.errors)[0] : null;
                throw new Error(typeof firstError?.message === 'string' && firstError.message !== ''
                    ? firstError.message
                    : apiErrorMessage(errorData));
            }

            // Clear form and reset state
            this.clear();
            this.clearTripData();
            this.close();

            // Show success and redirect to user's trips page
            this.announce(t('common.post_trip_shared'));

            setTimeout(() => {
                window.location.href = '/app/trips?u=' + encodeURIComponent(this.username);
            }, 500);
        },

        async savePost() {
            // Check if any photos failed to upload
            const failedPhotos = this.photos.filter(p => p.error);
            if (failedPhotos.length > 0) {
                throw new Error(t('common.post_photos_failed'));
            }

            await this.awaitQueuedUploads();

            // Get auth token (using global function from getApiToken.js)
            const token = await window.getApiToken();
            if (!token) {
                this.isSubmittingPost = false;
                throw new Error(t('common.post_auth_failed'));
            }

            // Collect already-uploaded photo IDs
            const photoIds = this.photos
                .filter(p => p.uploadedPhotoId)
                .map(p => p.uploadedPhotoId);

            // Create post
            const result = await this.createPost(token, photoIds);

            // Clear form
            this.isSubmittingPost = false;
            this.clear();
            this.close();

            // Navigate to posts section - force reload if already on the same page
            const targetUrl = '/' + encodeURIComponent(this.username) + '#posts';
            const currentPath = window.location.pathname;
            const targetPath = '/' + encodeURIComponent(this.username);

            if (currentPath === targetPath || currentPath === targetPath + '/') {
                window.location.reload();
            } else {
                window.location.href = targetUrl;
            }
        },

        // A post pressed while photos upload waits for them. The wait ends on success, a failure,
        // the timeout or a cancel, and the user can remove photos meanwhile.
        async awaitQueuedUploads() {
            if (!this.photos.some(p => p.uploading)) return;
            this.isSubmittingPost = true;
            this._queueCancelled = false;
            this.error = null;

            await this.waitForUploadsToComplete();

            if (this._queueCancelled) {
                const cancelled = new Error('cancelled');
                cancelled.queueCancelled = true;
                throw cancelled;
            }
            if (this.photos.some(p => p.error)) {
                throw new Error(t('common.post_photos_failed'));
            }
            if (this.photos.some(p => p.uploading)) {
                throw new Error(t('common.post_upload_timeout'));
            }
            if (!this.hasContent && !this.hasPhotos) {
                throw new Error(t('common.post_save_failed'));
            }
        },

        cancelQueuedSubmit() {
            const resolve = this._uploadWaitResolve;
            this._queueCancelled = true;
            this.clearUploadTimers();
            resolve?.();
        },

        async waitForUploadsToComplete() {
            return new Promise((resolve) => {
                this._uploadWaitResolve = resolve;
                this.uploadCheckInterval = setInterval(() => {
                    const stillUploading = this.photos.filter(p => p.uploading);
                    if (stillUploading.length === 0) {
                        this.clearUploadTimers();
                        resolve();
                    }
                }, this.UPLOAD_CHECK_INTERVAL_MS);

                this.uploadTimeout = setTimeout(() => {
                    this.clearUploadTimers();
                    resolve();
                }, this.UPLOAD_TIMEOUT_MS);
            });
        },

        clearUploadTimers() {
            if (this.uploadCheckInterval) {
                clearInterval(this.uploadCheckInterval);
                this.uploadCheckInterval = null;
            }
            if (this.uploadTimeout) {
                clearTimeout(this.uploadTimeout);
                this.uploadTimeout = null;
            }
            this._uploadWaitResolve = null;
        },

        destroy() {
            // A queued save settles as cancelled, so its promise does not outlive the component.
            this._closeAfterCancel = false;
            this.cancelQueuedSubmit();
            this.clearUploadTimers();

            clearTimeout(this._flashTimer);
            this.cancelCitySearch();
            this.stopDialogWatchers();
            this._returnFocus = null;

            // Release the scroll lock the open overlay holds
            if (this.isOpen) {
                this.isOpen = false;
                unlockBodyScroll();
            }

            this._textareaObserver?.disconnect();
            this._textareaObserver = null;

            // Remove the saved-field listener
            if (this._savedFieldHandler) {
                window.removeEventListener(editorBase.SAVED_EVENT, this._savedFieldHandler);
                this._savedFieldHandler = null;
            }

            this.dropPhotoWork();
        },

        async createPost(token, photoIds) {
            const data = {
                message: this.content.trim(),
                view_acl: this.visibility,
                feed_type: 'user',
                feed_owner_id: 'self'
            };

            if (this.title.trim()) {
                data.title = this.title.trim();
            }

            if (photoIds.length > 0) {
                data.photo_ids = photoIds.join(',');
            }

            const response = await fetch(this.ENDPOINTS.POST_API, {
                method: 'POST',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                // A phone-gate refusal is not a failed post. It sends the user
                // to the verify page, and the composer must not paint an error
                // over a navigation already under way.
                if (await followApiPhoneGateRefusal(response)) {
                    const refusal = new Error(PHONE_VERIFICATION_REQUIRED);
                    refusal.phoneGateRefusal = true;
                    throw refusal;
                }

                throw new Error(apiErrorMessage(await response.json().catch(() => ({}))));
            }

            return await response.json();
        },

        async updatePost() {
            // Check if any photos failed to upload
            const failedPhotos = this.photos.filter(p => p.error);
            if (failedPhotos.length > 0) {
                throw new Error(t('common.post_photos_failed'));
            }

            await this.awaitQueuedUploads();

            const token = await window.getApiToken();
            if (!token) {
                this.isSubmittingPost = false;
                throw new Error(t('common.post_auth_failed'));
            }

            // Collect ALL photo IDs that should remain on the post (existing + newly uploaded)
            // The API will diff this against current photos:
            // - Photos in new list but not in old = add
            // - Photos in old list but not in new = delete
            const allPhotoIds = this.photos
                .filter(p => p.uploadedPhotoId)
                .map(p => p.uploadedPhotoId);

            // Build update payload
            const data = {
                message: this.content.trim(),
                view_acl: this.visibility
            };

            if (this.title.trim()) {
                data.title = this.title.trim();
            }

            // Send all photo IDs that should be on the post
            // API will diff against existing to determine adds/deletes
            if (allPhotoIds.length > 0) {
                data.photo_ids = allPhotoIds.join(',');
            } else if (this.photosToDelete.length > 0) {
                // Empty string explicitly removes all photos
                data.photo_ids = '';
            }

            const response = await fetch(`${this.ENDPOINTS.POST_API}/${this.editPostId}`, {
                method: 'PUT',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                if (await followApiPhoneGateRefusal(response)) {
                    const refusal = new Error(PHONE_VERIFICATION_REQUIRED);
                    refusal.phoneGateRefusal = true;
                    throw refusal;
                }
                throw new Error(apiErrorMessage(await response.json().catch(() => ({}))));
            }

            this.isSubmittingPost = false;
            this.showToast(t('common.post_updated_success'));

            // Call completion callback if provided (for inline editing)
            if (this.onCompleteCallback) {
                setTimeout(() => {
                    if (typeof this.onCompleteCallback === 'function') {
                        this.onCompleteCallback();
                    } else if (typeof this.onCompleteCallback === 'string') {
                        // Execute parent Alpine method by name
                        this.$dispatch('post-updated');
                    }
                }, 300);
            } else {
                // Default: redirect to post display page
                setTimeout(() => {
                    window.location.href = `/app/${this.editFeedOwnerId}/posts/${this.editPostId}`;
                }, 500);
            }
        },

        // The overlay is closed by the time a status or trip saves, so its result shows outside it.
        announce(message) {
            if (typeof window.showSuccess === 'function') {
                window.showSuccess(message);
            } else {
                this.showToast(message);
            }
        },

        showToast(message) {
            // Show success state on the submit button instead of floating toast
            this.successMessage = message;
            this.showSuccess = true;

            // Reset after 2 seconds
            setTimeout(() => {
                this.showSuccess = false;
                this.successMessage = '';
            }, 2000);
        }
    };
}
